/* components/ui/Modal.jsx — универсальный хром модального окна (без доменного контента).
   Размеры xs/sm/md/lg/xl. На мобильной полосе (xs, ≤719.98px) → fullscreen sheet
   (медиа-правило в components.css, согласовано с Drawer).
   focus-trap, body-scroll-lock, ESC, backdrop-click.
   Пропсы zIndex/role/autoFocusSelector — для надстроек (ConfirmProvider: role=alertdialog, z-confirm).
   Имя диалога (K-09): заголовок связывается САМ через useId → aria-labelledby; labelledBy —
   если заголовок свой; ariaLabel — если заголовка нет. describedBy → aria-describedby
   (текст подтверждения alertdialog).
   Слоты: title + onClose | children (body) | footer (footerLeft + actions). */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from './Icon.jsx';
import { getOverlayRoot, overlay } from '../../lib/overlay.jsx';
import { useDialogHold } from '../../lib/dialogHold.js';

  const { useRef, useId } = React;
  const { useFocusTrap, useScrollLock } = overlay;

  function Modal({ open, onClose, title, size = 'md', children, footer, footerLeft,
                   closeOnBackdrop = true, labelledBy, describedBy, ariaLabel, zIndex = 'var(--z-modal)',
                   role = 'dialog', autoFocusSelector, className }) {
    const panelRef = useRef(null);
    const autoTitleId = useId();
    useScrollLock(open);
    useFocusTrap(panelRef, { active: open, onEscape: onClose, autoFocusSelector });
    // открытый диалог держит вкладку в её пространстве (lib/dialogHold.js, S10)
    useDialogHold(open);
    if (!open) return null;

    const hasTitle = title != null && title !== false && title !== '';
    const titleId = labelledBy || (hasTitle ? autoTitleId : undefined);

    return ReactDOM.createPortal(
      <div className="overlay overlay--modal" style={{ zIndex }}>
        <div className="overlay__backdrop" onClick={closeOnBackdrop ? onClose : undefined} />
        <div className={`modal modal--${size}` + (className ? ' ' + className : '')} role={role} aria-modal="true"
             aria-labelledby={titleId} aria-describedby={describedBy}
             aria-label={titleId ? undefined : ariaLabel} ref={panelRef}>
          {(hasTitle || onClose) && (
            <div className="modal__head">
              <h2 className="modal__title t-h3" id={hasTitle ? titleId : undefined}>{title}</h2>
              {/* закрыть нельзя (onClose не передан: вопрос сторожа, пока форма записывает) — «×»
                  выключен вместе с кнопками ответа, а не молчит на нажатие (QA Г3, I04) */}
              <button className="modal__close" onClick={onClose} aria-label="Закрыть" disabled={typeof onClose !== 'function'}>
                <Icon name="close" size={18} />
              </button>
            </div>
          )}
          <div className="modal__body">{children}</div>
          {(footer || footerLeft) && (
            <div className="modal__foot">
              <div className="modal__foot-left">{footerLeft}</div>
              <div className="modal__foot-actions cluster cluster-4">{footer}</div>
            </div>
          )}
        </div>
      </div>,
      getOverlayRoot()
    );
  }

export { Modal };
